import { awaitLeaveEcommerceDesignerBetterAuthResync } from '~~/layers/base/utils/ecommerceDesignerBetterAuthInjunction'
import { isEcommerceDesignerRoute } from '~~/layers/base/utils/isEcommerceDesignerRoute'

/**
 * Keeps `useProfileStore` aligned with Better Auth: clear when logged out / anonymous, refetch
 * when a real session exists so navbar and other clients read roles without per-mount `useFetch`.
 *
 * Also warms return addresses on sign-in so Account’s “N saved” row and `/profile/addresses`
 * hit the in-memory cache instead of a cold `GET` after the user is already in the app.
 *
 * Crane Manager resource-check cache clear on logout lives in
 * `layers/crane-manager/plugins/clear-admin-check-on-logout.client.ts` (web-only layer).
 */
export default defineNuxtPlugin(() => {
    const route = useRoute()
    const { isLoggedIn, isSessionPending } = useBetterAuth()
    const profileStore = useProfileStore()
    const datadog = useDatadog()

    watch(
        [isLoggedIn, isSessionPending, () => route.fullPath],
        async () => {
            await awaitLeaveEcommerceDesignerBetterAuthResync()

            // TK-13530 injunction — re-enable profile sync when purchase designer uses Better Auth.
            if (isEcommerceDesignerRoute(route)) {
                return
            }
            const pending = isSessionPending.value
            const loggedIn = isLoggedIn.value
            // Pending looks logged-out; do not wipe sessionStorage hydrate on refresh.
            if (pending) {
                return
            }
            if (!loggedIn) {
                profileStore.clearProfile()
                return
            }
            await profileStore.ensureProfileLoaded()
            // Soft-fail: transport/API errors must not become unhandled rejections on offline cold
            // start, but still report to Datadog for observability.
            void profileStore.ensureAddressesLoaded().catch((error: unknown) => {
                const err = error instanceof Error ? error : new Error(String(error))
                datadog.logError(err, {
                    feature: 'profile',
                    action: 'ensureAddressesLoaded',
                    phase: 'auth-sync-plugin',
                })
            })
        },
        { immediate: true }
    )
})
